/**
 * src/components/agent/TurnBubble.jsx — ship's-log turn rendering
 *
 * 2026-04-21 redesign (F7 usability pass). Previous bubble-first layout
 * put the timestamp + source on a tiny meta line beneath the content,
 * which made it impossible to scan past conversations for context or
 * identify who said what when multiple users share the tool. The CEO's
 * mental model is a ship's log — timestamped entries with clear authors
 * — not an iMessage thread.
 *
 * New layout per turn:
 *
 *   [Initial] Name · 08:07 · [mic icon if voice]
 *             ──────────────────────────────────
 *             Content text flows here, wrapping
 *             at the container width. Navy accent
 *             rail on the left for assistant entries.
 *
 * Multi-user: `turn.speaker_label` wins if present; otherwise 'You' for
 * user role and the provided `assistantName` prop for assistant (defaults
 * to 'Nora' to match the voice picker). When shop workers or sales agents
 * start using voice mode, backend should stamp `speaker_label` on their
 * rows so rendering is automatic.
 *
 * Performance note: still wrapped in React.memo with a custom comparator.
 * Identity checks match the prior hook (id / role / content / source /
 * created_at / speaker_label) plus the new `assistantName` prop. Without
 * this, SSE token streams that upsert the streaming turn would re-render
 * every prior bubble on every delta — previously caused TTS audible gaps.
 */

import { memo } from 'react';
import { Mic } from 'lucide-react';
import { splitWords } from '../../lib/voice/karaoke';

// TICKET-073 (karaoke) — the word currently being spoken by TTS. MUST be
// unmistakable in BOTH light AND dark themes (CEO 2026-06-09: "no karaoke
// effect at all" on iPhone). The prior treatment was color:var(--navy) text
// with NO background — in DARK mode --navy (#1C3F75) is a DARK navy that sits
// near-invisible on the dark --grad overlay surface (it actually reads DARKER
// than the surrounding --t1 near-white body text). Token rule: never reuse one
// token for both color AND background. Here the pill background is the navy
// accent wash (--accent-bg, defined light+dark) and the text is the saturated
// --accent (navy in light, light-blue #6BA6F5 in dark) — real contrast on both
// themes. Rounded pill + bold makes the spoken word pop as it advances.
const activeWordStyle = {
  color: 'var(--accent)',
  background: 'var(--accent-bg)',
  fontWeight: 700,
  borderRadius: 4,
  padding: '0 2px',
  // box-decoration so the pill wraps cleanly if the active word breaks a line.
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
};

// Initial badge — 22px circle with single letter. Tinted by role.
const INITIAL_SIZE = 22;

function getInitial(name) {
  if (!name || typeof name !== 'string') return '?';
  const clean = name.trim();
  if (!clean) return '?';
  return clean[0].toUpperCase();
}

function resolveSpeaker({ turn, assistantName }) {
  if (!turn || !turn.role) return { name: '', initial: '?' };
  if (turn.speaker_label && turn.speaker_label.trim()) {
    return { name: turn.speaker_label.trim(), initial: getInitial(turn.speaker_label) };
  }
  if (turn.role === 'user') return { name: 'You', initial: 'Y' };
  if (turn.role === 'assistant') {
    const name = (assistantName && assistantName.trim()) || 'Nora';
    return { name, initial: getInitial(name) };
  }
  return { name: 'System', initial: 'i' };
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function TurnBubbleImpl({ turn, assistantName, isStreaming, karaokeActive = false, karaokeWordIndex = -1 }) {
  if (!turn || !turn.role) return null;
  const role = turn.role;
  const source = turn.source || 'text';
  const isUser = role === 'user';
  const isSystem = role === 'system';
  const isAssistant = role === 'assistant';

  const { name, initial } = resolveSpeaker({ turn, assistantName });
  const content = turn.content || turn.text || '';

  // System messages keep the old centered-pill treatment — they're
  // process-status, not dialog, and don't need the log-entry scaffolding.
  if (isSystem) {
    return (
      <div
        data-testid="turn-bubble"
        data-speaker="system"
        data-source={source}
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '4px 0',
        }}
      >
        <div
          style={{
            background: 'var(--surface-muted)',
            color: 'var(--tm)',
            fontSize: 12,
            padding: '6px 12px',
            borderRadius: 10,
            border: '1px solid var(--bdr)',
            fontStyle: 'italic',
            maxWidth: '80%',
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  // Initial badge: user = outlined navy, assistant = filled navy.
  const badgeStyle = {
    flex: '0 0 auto',
    width: INITIAL_SIZE,
    height: INITIAL_SIZE,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    background: isAssistant ? 'var(--navy)' : 'transparent',
    color: isAssistant ? 'var(--card)' : 'var(--navy)',
    border: isAssistant ? 'none' : '1.5px solid var(--navy)',
    userSelect: 'none',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: 'var(--t2)',
    marginBottom: 6,
  };

  const nameStyle = {
    fontWeight: 600,
    color: 'var(--t1)',
    letterSpacing: 0.1,
  };

  const timeStyle = {
    color: 'var(--tm)',
    fontVariantNumeric: 'tabular-nums',
  };

  const separatorStyle = {
    color: 'var(--bdr)',
    fontWeight: 400,
    opacity: 0.6,
  };

  const micIconStyle = {
    color: 'var(--tm)',
    display: 'inline-flex',
    alignItems: 'center',
  };

  // The log-entry body. Navy rail for assistant (left border), nothing
  // for user (the initial badge + right-aligned time carry the weight).
  const bodyWrapperStyle = {
    paddingLeft: isAssistant ? 12 : INITIAL_SIZE + 8,
    borderLeft: isAssistant ? '2px solid var(--navy)' : 'none',
    marginLeft: isAssistant ? (INITIAL_SIZE - 2) / 2 : 0, // align rail under badge center
    // Streaming pulse — the assistant turn currently receiving SSE
    // tokens gets a subtle opacity breath so the user knows more is
    // coming. prefers-reduced-motion users just see full opacity.
    animation: isAssistant && isStreaming ? 'acn-turn-stream 2s ease-in-out infinite' : undefined,
  };

  const bodyTextStyle = {
    fontSize: 14,
    lineHeight: 1.55,
    color: 'var(--t1)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  };

  return (
    <div
      data-testid="turn-bubble"
      data-speaker={role}
      data-source={source}
      data-streaming={isStreaming ? 'true' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '6px 0',
      }}
    >
      <div style={headerStyle}>
        <span style={badgeStyle} aria-hidden="true">{initial}</span>
        <span style={nameStyle} data-testid="turn-speaker-name">{name}</span>
        <span style={separatorStyle}>·</span>
        {turn.created_at && (
          <time
            dateTime={turn.created_at}
            style={timeStyle}
            data-testid="turn-timestamp"
          >
            {formatTime(turn.created_at)}
          </time>
        )}
        {source === 'voice' && (
          <>
            <span style={separatorStyle}>·</span>
            <span style={micIconStyle} aria-label="voice" title="Voice message">
              <Mic size={11} strokeWidth={2} />
            </span>
          </>
        )}
      </div>
      <div style={bodyWrapperStyle}>
        <div style={bodyTextStyle} data-testid="turn-content">
          {karaokeActive && isAssistant && Number(karaokeWordIndex) >= 0
            ? splitWords(content).map((w, i) => (
              <span
                key={`${i}-${w}`}
                data-testid={`turn-word-${i}`}
                data-karaoke-active={i === Number(karaokeWordIndex) ? 'true' : undefined}
                style={i === Number(karaokeWordIndex) ? activeWordStyle : undefined}
              >
                {w}{i < splitWords(content).length - 1 ? ' ' : ''}
              </span>
            ))
            : content}
        </div>
      </div>
    </div>
  );
}

function areEqual(prev, next) {
  if (prev.assistantName !== next.assistantName) return false;
  if (prev.isStreaming !== next.isStreaming) return false;
  // TICKET-073 (karaoke) — the active word advances via these props; if the
  // comparator ignores them, memo freezes the highlight on word 0.
  if (prev.karaokeActive !== next.karaokeActive) return false;
  if (prev.karaokeWordIndex !== next.karaokeWordIndex) return false;
  const a = prev.turn;
  const b = next.turn;
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id
    && a.role === b.role
    && a.source === b.source
    && a.created_at === b.created_at
    && a.speaker_label === b.speaker_label
    && (a.content || a.text || '') === (b.content || b.text || '')
  );
}

const TurnBubble = memo(TurnBubbleImpl, areEqual);
export default TurnBubble;
